import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader, DataTable } from '../../components/office';
import Person from '../../components/people/Person';
import { staffApi, authApi } from '@verlo/shared/services/api';
import { useAuth } from '@verlo/shared/contexts/AuthContext';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Notice } from '@verlo/shared/components/ui/notice';
import { Signal } from '@verlo/shared/components/ui/signal';
import { Skeleton } from '@verlo/shared/components/ui/skeleton';
import { Segmented } from '@verlo/shared/components/ui/segmented';
import { SearchField } from '@verlo/shared/components/ui/search-field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@verlo/shared/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@verlo/shared/components/ui/sheet';
import { useAuthoringSite, useSite } from '../../contexts/SiteContext';
import { phoneProblem, tidyPhone } from '@verlo/shared/lib/phone';

// Team: Staff and the Org chart, one page (Back office sweep, Oct 2026: D1, mock "Team").
// List and Chart are two views of the same people; a person in either opens the same panel,
// which holds everything we have on them: their role, and both ways they sign in (a till PIN,
// a back-office login), which are two credentials on their one account.

// Someone's one role: what they can do on the till and in the back office (Oct 2026). It is
// kept on the person and written through to their login. Who may give whom which role is the
// API's rule (`team_roles`): below your own, to someone below you. This only shows it.
// The org chart's Level is gone: supervisor and team lead became shift lead.
export const ROLES = [
  { value: 'server', label: 'Server', hint: 'Takes orders and payments, runs food' },
  { value: 'bar_staff', label: 'Bar', hint: 'Takes orders and payments at the bar' },
  { value: 'kitchen', label: 'Kitchen', hint: 'Moves kitchen tickets on' },
  { value: 'shift_lead', label: 'Shift lead', hint: 'All of those, and voids and discounts' },
  { value: 'manager', label: 'Manager', hint: 'Cash, refunds, menu, stock and the rota' },
  { value: 'general_manager', label: 'General manager', hint: 'Also wages, the books and settings' },
  { value: 'area_manager', label: 'Area manager', hint: 'Several sites' },
  { value: 'operations', label: 'Operations', hint: 'Every site' },
  { value: 'owner', label: 'Owner', hint: 'Everything, billing too' },
];
export const RANK = { server: 0, bar_staff: 0, kitchen: 0, shift_lead: 1, manager: 2, general_manager: 3, area_manager: 4, operations: 4, owner: 5 };
const rankOf = (p) => RANK[p?.rbac_role] ?? 0;
export const roleLabel = (v) => ROLES.find((r) => r.value === v)?.label || 'Server';
// Their login signs in as something other than their role on Team: data from before the
// role lived here. Never changed for them; flagged, for someone above them to settle.
export const mismatched = (p) => !!p.account_role && p.account_role !== p.rbac_role;
const CONTRACTS = [
  { value: 'full_time', label: 'Full time' },
  { value: 'part_time', label: 'Part time' },
  { value: 'casual', label: 'Casual or zero hours' },
];
// Offered as you type a role. The venue's own roles (whatever its people already have)
// come first, so a Head Chef reads as Head Chef.
const COMMON_ROLES = ['General Manager', 'Assistant Manager', 'Head Chef', 'Sous Chef', 'Chef', 'Server',
  'Bartender', 'Host', 'Kitchen Porter', 'Cleaner'];

const blank = { name: '', email: '', phone: '', role: '', rbac_role: 'server', reports_to: '',
  hourly_rate: 12, employment_type: 'full_time', start_date: '' };

const money = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v || 0);

export default function TeamPage() {
  const authoring = useAuthoringSite();
  const { current: picked, sites } = useSite();
  // The site in the header, or the only one there is.
  const current = picked || ((sites || []).length === 1 ? sites[0] : null);
  const { isAdmin } = useAuth();
  const canManage = isAdmin();
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'chart' ? 'chart' : 'list';
  const [staff, setStaff] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState(() => params.get('q') || '');  // ?q= from Jump to
  const [role, setRole] = useState('all');
  const [open, setOpen] = useState(null);        // the person in the panel, or 'new'
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [collapsed, setCollapsed] = useState(new Set());
  const [newRoles, setNewRoles] = useState(['server']);   // what you may give someone new

  const load = async () => {
    try {
      const fresh = (await staffApi.getAll(!showInactive)).data || [];
      setStaff(fresh);
      // The panel shows the person as they are now, not as they were when it opened.
      setOpen((o) => (o && o !== 'new' ? fresh.find((p) => p.id === o.id) || o : o));
    } catch { toast.error("Couldn't load the team"); setStaff([]); }
  };
  useEffect(() => { load(); }, [showInactive]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { staffApi.roles().then((r) => setNewRoles(r.data?.assignable || [])).catch(() => {}); }, []);

  const people = staff || [];
  const byId = useMemo(() => Object.fromEntries(people.map((p) => [p.id, p])), [people]);
  const roles = useMemo(() => [...new Set(people.map((p) => p.role).filter(Boolean))].sort(), [people]);
  const roleChoices = useMemo(() => [...new Set([...roles, ...COMMON_ROLES])], [roles]);
  const shown = people.filter((p) => (role === 'all' || p.role === role)
    && (!search || `${p.name} ${p.email} ${p.role}`.toLowerCase().includes(search.toLowerCase())));

  const openPerson = (p) => {
    setOpen(p);
    setForm({ ...blank, ...Object.fromEntries(Object.keys(blank).map((k) => [k, p[k] ?? blank[k]])), reports_to: p.reports_to || '' });
  };
  const openNew = (reportsTo = '') => { setOpen('new'); setForm({ ...blank, reports_to: reportsTo, rbac_role: newRoles[0] || 'server' }); };

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error('A name and an email, please'); return; }
    if (!form.role.trim()) { toast.error('What do they do? Give them a job title'); return; }
    setSaving(true);
    const { rbac_role: rbacRole, ...rest } = form;
    const body = { ...rest, phone: tidyPhone(form.phone), name: form.name.trim(), role: form.role.trim(), reports_to: form.reports_to || null, start_date: form.start_date || null };
    try {
      if (open === 'new') await staffApi.create({ ...body, rbac_role: rbacRole }, authoring.site);
      else {
        await staffApi.update(open.id, body);
        if (rbacRole && rbacRole !== open.rbac_role) await staffApi.setRole(open.id, rbacRole);
      }
      toast.success(open === 'new' ? `${body.name} is on the team` : `${body.name} saved`);
      setOpen(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save them");
    } finally { setSaving(false); }
  };

  // Nobody is deleted: their shifts and timesheets still name them. Deactivating stops their
  // PIN on every till and signs them out; bringing them back needs a new PIN.
  const toggleActive = async (p) => {
    try {
      await staffApi.toggleStatus(p.id, !p.is_active);
      toast.success(p.is_active ? `${p.name} is off the team. Their shifts and timesheets stay.` : `${p.name} is back. Send them a new PIN for the till.`);
      setOpen(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't change that"); }
  };

  const managers = people.filter((p) => p.is_active && rankOf(p) > 0 && p.id !== open?.id);
  const flagged = people.filter((p) => p.is_active && mismatched(p));

  // A login that disagrees with Team: bring it into line, either way, one click.
  const settle = async (p, role) => {
    try {
      await staffApi.setRole(p.id, role);
      toast.success(`${p.name} is ${roleLabel(role).toLowerCase()} on Team and on the till`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't change that"); }
  };

  return (
    <div className="space-y-5" data-testid="team-page">
      <PageHeader
        title="Team"
        purpose={`Everyone who works at ${current?.name || 'your restaurant'}, how they fit together, and what we hold on each of them.`}
        actions={(<>
          <Segmented
            value={view}
            onChange={(v) => setParams(v === 'chart' ? { view: 'chart' } : {}, { replace: true })}
            options={[{ value: 'list', label: 'List', testId: 'team-view-list' }, { value: 'chart', label: 'Chart', testId: 'team-view-chart' }]}
          />
          <Button className="bg-verlo hover:bg-verlo-hover" onClick={() => openNew()} disabled={!authoring.ready}
            title={authoring.reason || undefined} data-testid="add-staff-btn">Add a person</Button>
        </>)}
      />

      {canManage && flagged.length > 0 && (
        <div className="space-y-2" data-testid="team-mismatches">
          {flagged.map((p) => <Mismatch key={p.id} person={p} onSettle={settle} />)}
        </div>
      )}

      {staff === null ? <Skeleton className="h-64" /> : view === 'list' ? (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <SearchField className="min-w-[220px] max-w-sm flex-1" placeholder="Search the team" value={search}
              onChange={(e) => setSearch(e.target.value)} onClear={() => setSearch('')} />
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="w-48" data-testid="team-role-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All job titles</SelectItem>
                {roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 text-[13px] text-ink-soft">
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="accent-verlo" />
              Show people who've left
            </label>
          </div>
          <DataTable
            testId="staff-table"
            columns={[
              { key: 'name', label: 'Person', sortable: true, render: (p) => (
                <Person name={p.name} role={p.role} after={!p.is_active && <Signal tone="stop" dot={false}>left</Signal>} />) },
              // Every column sorts (Chris's review L12). Reports to shows only when somebody
              // reports to somebody (L11). A PIN is never shown, only whether it's gone (L14).
              { key: 'rbac_role', label: 'Role', muted: true, sortable: true, sortValue: (p) => -rankOf(p), render: (p) => (
                <span className="inline-flex items-center gap-2">{roleLabel(p.rbac_role)}
                  {mismatched(p) && <Signal tone="warn" dot={false}>signs in as {roleLabel(p.account_role).toLowerCase()}</Signal>}
                </span>) },
              ...(people.some((p) => p.reports_to) ? [{ key: 'reports_to', label: 'Reports to', muted: true, sortable: true, sortValue: (p) => byId[p.reports_to]?.name || '~', render: (p) => byId[p.reports_to]?.name || '—' }] : []),
              { key: 'phone', label: 'Phone', muted: true, sortable: true, sortValue: (p) => p.phone || '~', render: (p) => tidyPhone(p.phone) || <span className="text-ink-faint">Not given</span> },
              { key: 'signs_in', label: 'Signs in with', sortable: true, sortValue: (p) => signsIn(p), render: (p) => signsIn(p) || <span className="text-ink-faint">Not yet</span> },
            ]}
            rows={shown}
            rowKey="id"
            onRowClick={openPerson}
            empty={{ word: 'Team', title: search || role !== 'all' ? 'Nobody matches' : 'Nobody on the team yet', children: 'Add the first person, then send them a PIN for the till.' }}
          />
          <p className="text-[12.5px] text-ink-faint">{people.length} {people.length === 1 ? 'person' : 'people'} · Chart shows the same people by who they report to</p>
        </>
      ) : (
        <Chart people={people.filter((p) => p.is_active)} collapsed={collapsed}
          onToggle={(id) => setCollapsed((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; })}
          onOpen={openPerson} />
      )}

      <Sheet open={!!open} onOpenChange={(o) => { if (!o) setOpen(null); }}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md" data-testid="team-panel">
          <SheetHeader>
            <SheetTitle>{open === 'new' ? 'Add a person' : open?.name}</SheetTitle>
            {open && open !== 'new' && (
              <SheetDescription>{open.role}{byId[open.reports_to] ? ` · reports to ${byId[open.reports_to].name}` : ''}</SheetDescription>
            )}
          </SheetHeader>
          <div className="mt-5 grid grid-cols-2 gap-x-3 gap-y-4">
            <Field label="Name" wide><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name-input" /></Field>
            <Field label="Job title">
              <Input list="team-roles" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="e.g. Head Chef" data-testid="staff-role-input" />
              <datalist id="team-roles">{roleChoices.map((r) => <option key={r} value={r} />)}</datalist>
            </Field>
            <Field label="Role">
              <RolePicker person={open} value={form.rbac_role} onChange={(v) => setForm({ ...form, rbac_role: v })}
                allowed={open === 'new' ? newRoles : open?.assignable_roles || []} />
            </Field>
            <Field label="Reports to" wide>
              <Select value={form.reports_to || 'none'} onValueChange={(v) => setForm({ ...form, reports_to: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nobody</SelectItem>
                  {managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} · {m.role}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Phone">
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} onBlur={() => setForm((f) => ({ ...f, phone: tidyPhone(f.phone) }))} inputMode="tel" placeholder="Add a number" />
              {phoneProblem(form.phone) && <p className="text-[12px] text-signal-warn">{phoneProblem(form.phone)}</p>}
            </Field>
            <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email-input" /></Field>
            <Field label="Pay, an hour">
              <Input type="number" step="0.05" min="0" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: parseFloat(e.target.value) || 0 })} />
            </Field>
            <Field label="Contract">
              <Select value={form.employment_type} onValueChange={(v) => setForm({ ...form, employment_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CONTRACTS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Started"><Input type="date" value={form.start_date || ''} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></Field>
            <Field label="Site"><div className="flex h-9 items-center rounded-md border border-input bg-surface-2 px-3 text-sm text-ink">{current?.name || '—'}</div></Field>
          </div>

          {open && open !== 'new' && open.is_active && mismatched(open) && canManage && (
            <div className="mt-5"><Mismatch person={open} onSettle={settle} stacked /></div>
          )}
          {/* Your own card offers you your own PIN; someone else's, if you outrank them, or if
              you're an owner and they're an owner with no login yet (the API's `may_issue`). */}
          {open && open !== 'new' && open.is_active && canManage && (open.may_issue || open.is_you) && (
            <SigningIn person={open} isMe={open.is_you} onDone={load} />
          )}

          <div className="mt-6 flex items-center justify-between gap-2 border-t border-hairline pt-4">
            {/* Only someone above them (the API's rank rule); never yourself. */}
            {open && open !== 'new' && open.may_manage ? (
              <Button variant="ghost" className="text-signal-stop hover:text-signal-stop" onClick={() => toggleActive(open)} data-testid="staff-status-btn">
                {open.is_active ? 'They’ve left' : 'Bring them back'}
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setOpen(null)}>Cancel</Button>
              <Button className="bg-verlo hover:bg-verlo-hover" onClick={save} disabled={saving} data-testid="save-staff-btn">{saving ? 'Saving…' : 'Save'}</Button>
            </div>
          </div>
          {open && open !== 'new' && open.is_active && (
            <p className="mt-2 text-[12px] text-ink-faint">
              {open.may_manage && '“They’ve left” stops their PIN on every till and signs them out of the back office. Their shifts and timesheets stay. '}
              Pay {money(open.hourly_rate)} an hour.
            </p>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Field({ label, wide, children }) {
  return (
    <div className={`space-y-1.5 ${wide ? 'col-span-2' : ''}`}>
      <Label className="text-[12.5px] text-ink-soft">{label}</Label>
      {children}
    </div>
  );
}

// How someone signs in, in a word, for the list.
function signsIn(p) {
  const office = { active: 'password', invited: 'invite sent', off: 'password (off)' }[p.back_office];
  return [p.has_pin && 'PIN', office].filter(Boolean).join(' and ');
}

// Their role. Every role is listed and the ones you can't give are greyed; on someone you
// can't change (above you, or you), the whole picker is.
function RolePicker({ person, value, onChange, allowed }) {
  const locked = person && person !== 'new' && !allowed.length;
  return (
    <Select value={value} onValueChange={onChange} disabled={locked}>
      <SelectTrigger data-testid="staff-rbac-role"><SelectValue>{roleLabel(value)}</SelectValue></SelectTrigger>
      <SelectContent>
        {ROLES.map((r) => (
          <SelectItem key={r.value} value={r.value} disabled={!allowed.includes(r.value) && r.value !== value}>
            {r.label}<span className="ml-2 text-ink-faint">{r.hint}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// "Alex Morgan: General manager on Team, signs in as Server." Nothing was changed for them;
// someone above them settles it, either way.
function Mismatch({ person, onSettle, stacked = false }) {
  const [busy, setBusy] = useState(false);
  const can = person.assignable_roles || [];
  const go = async (role) => { setBusy(true); try { await onSettle(person, role); } finally { setBusy(false); } };
  // The main button never takes away what someone can do now: when Team says less than their
  // login, keeping the login's role is the one in brand colour.
  const down = (RANK[person.rbac_role] ?? 0) < (RANK[person.account_role] ?? 0);
  const [primary, other] = down ? [person.account_role, person.rbac_role] : [person.rbac_role, person.account_role];
  const word = (role) => (role === person.account_role ? `Keep ${roleLabel(role).toLowerCase()}` : `Make them ${roleLabel(role).toLowerCase()}`);
  const buttons = can.includes(primary) || can.includes(other) ? (
    <div className="flex shrink-0 flex-wrap gap-2">
      {can.includes(other) && (
        <Button variant="ghost" size="sm" disabled={busy} onClick={() => go(other)} data-testid={`team-mismatch-${other === person.rbac_role ? 'fix' : 'keep'}-${person.id}`}>
          {word(other)}
        </Button>
      )}
      {can.includes(primary) && (
        <Button size="sm" className="bg-verlo hover:bg-verlo-hover" disabled={busy} onClick={() => go(primary)} data-testid={`team-mismatch-${primary === person.rbac_role ? 'fix' : 'keep'}-${person.id}`}>
          {word(primary)}
        </Button>
      )}
    </div>
  ) : null;
  return (
    <Notice tone="warn" word="Role" data-testid={`team-mismatch-${person.id}`}
      title={`${person.name}: ${roleLabel(person.rbac_role)} on Team, signs in as ${roleLabel(person.account_role)}.`}
      action={stacked ? null : buttons}>
      {buttons
        ? (down
          ? `Their login can do more than Team says. Keep them ${roleLabel(person.account_role).toLowerCase()}, or take them down to ${roleLabel(person.rbac_role).toLowerCase()} on the till and in the back office.`
          : `Make them ${roleLabel(person.rbac_role).toLowerCase()}? It changes what they can do on the till and in the back office.`)
        : 'Only someone above them can settle this.'}
      {stacked && buttons && <div className="mt-2">{buttons}</div>}
    </Notice>
  );
}

// Both ways they sign in, on their one account: a PIN for the till, a password for the back office.
function SigningIn({ person, isMe, onDone }) {
  return (
    <div className="mt-6 space-y-4 border-t border-hairline pt-4" data-testid="staff-signing-in">
      <div className="text-[12.5px] font-semibold uppercase tracking-[0.06em] text-ink-soft">Signing in</div>
      <PinBlock person={person} onDone={onDone} />
      {!isMe && <BackOfficeBlock person={person} onDone={onDone} />}
    </div>
  );
}

// The back office: invite them (a temporary password, emailed), or say where they are with it.
function BackOfficeBlock({ person, onDone }) {
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState(null);   // a temporary password, when no email went out
  useEffect(() => { setShown(null); }, [person.id]);
  const state = person.back_office;
  const hasEmail = !!person.email && !person.email.endsWith('@pin.local');

  const invite = async () => {
    setBusy(true);
    try {
      const { data } = await authApi.invite({ staff_id: person.id });
      if (data.linked && !data.invite_sent && !data.temporary_password) toast.success(`${person.name}'s existing login is now theirs on Team`);
      else if (data.temporary_password) setShown(data.temporary_password);
      else toast.success(`An invite is on its way to ${data.user.email}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send the invite"); } finally { setBusy(false); }
  };

  const say = {
    active: ['Signs in with a password', 'Their sessions, two-factor and switching it off are on Accounts'],
    invited: ['Invited, not signed in yet', 'A new invite makes a new temporary password'],
    off: ['Switched off', 'Switch it back on in Accounts'],
  }[state] || ['No back-office login', hasEmail ? 'An invite emails them a temporary password' : 'Add their email to invite them'];

  if (shown) {
    return (
      <div className="space-y-2 border border-hairline bg-surface-2 p-4" data-testid="staff-invite-shown">
        <div className="text-[13px] text-ink-soft">Email isn't set up here, so give {person.name} this temporary password. It won't be shown again, and stops working after 7 days.</div>
        <div className="flex items-center justify-between">
          <span className="font-mono text-sm text-ink" data-testid="staff-invite-password">{shown}</span>
          <Button variant="ghost" size="icon" title="Copy" onClick={() => { navigator.clipboard?.writeText(shown).catch(() => {}); toast.success('Copied'); }}><Copy size={14} /></Button>
        </div>
        <Button variant="outline" size="sm" onClick={() => setShown(null)}>Done</Button>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between gap-3" data-testid="staff-back-office">
      <div className="flex flex-col">
        <span className="text-[14px] font-semibold text-ink">Back office · {say[0]}</span>
        <span className="text-[12.5px] text-ink-faint">{say[1]}</span>
      </div>
      {(state == null || state === 'invited') && (
        <Button variant="outline" onClick={invite} disabled={busy || !hasEmail} data-testid={`staff-invite-${person.id}`}>
          {busy ? 'Sending…' : state === 'invited' ? 'Resend the invite' : 'Invite'}
        </Button>
      )}
    </div>
  );
}

// The PIN: whether they have one, and "Send a new PIN". It's emailed to them; with no email
// to send it to, it's shown here once, after you've re-entered your password (D4).
function PinBlock({ person, onDone }) {
  const [callerHasPin, setCallerHasPin] = useState(false);
  const [step, setStep] = useState(null);   // null | 'ask' (your PIN / password) | { pin }
  const [ask, setAsk] = useState(null);     // the server's reason it needs your password
  const [password, setPassword] = useState('');
  const [managerPin, setManagerPin] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { authApi.pinStatus().then((r) => setCallerHasPin(!!r.data?.has_pin)).catch(() => {}); }, []);
  useEffect(() => { setStep(null); setAsk(null); setPassword(''); setManagerPin(''); }, [person.id]);

  const send = async () => {
    if (callerHasPin && managerPin.length < 4) { setStep('ask'); return; }
    setBusy(true);
    try {
      const { data } = await authApi.pinSendForStaff({
        staff_id: person.id, ...(password ? { password } : {}), ...(callerHasPin ? { manager_pin: managerPin } : {}),
      });
      if (data.pin) setStep({ pin: data.pin });
      else { toast.success(`A new PIN is on its way to ${data.sent_to}`); setStep(null); }
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === 'password_required') { setAsk(d.message); setStep('ask'); }
      else toast.error((typeof d === 'string' && d) || "Couldn't send a new PIN");
    } finally { setBusy(false); setPassword(''); }
  };

  if (step && step.pin) {
    return (
      <div className="space-y-2 border border-hairline bg-surface-2 p-4" data-testid="staff-pin-shown">
        <div className="text-[13px] text-ink-soft">{person.name}'s new PIN. It won't be shown again; they choose their own when they first sign in.</div>
        <div className="flex items-center justify-between">
          <span className="font-mono text-2xl tracking-[0.3em] text-ink" data-testid="staff-pin-value">{step.pin}</span>
          <Button variant="ghost" size="icon" title="Copy" onClick={() => { navigator.clipboard?.writeText(step.pin); toast.success('Copied'); }}><Copy size={14} /></Button>
        </div>
        <Button variant="outline" size="sm" onClick={() => setStep(null)}>Done</Button>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="staff-pin-block">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col">
          <span className="text-[14px] font-semibold text-ink">Till · {person.has_pin ? 'PIN set' : 'No PIN yet'}</span>
          <span className="text-[12.5px] text-ink-faint">{person.has_pin ? `Signs in on the till as ${roleLabel(person.account_role || person.rbac_role).toLowerCase()}` : 'They can’t use the till until they have one'}</span>
        </div>
        {step !== 'ask' && (
          <Button variant="outline" onClick={send} disabled={busy} data-testid={`staff-pin-${person.id}`}>{busy ? 'Sending…' : 'Send a new PIN'}</Button>
        )}
      </div>
      {step === 'ask' && (
        <div className="space-y-3">
          {ask && <Notice tone="info" word="Once">{ask}</Notice>}
          {ask && (
            <div className="space-y-1.5"><Label className="text-[12.5px] text-ink-soft">Your password</Label>
              <Input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="staff-pin-password" /></div>
          )}
          {callerHasPin && (
            <div className="space-y-1.5"><Label className="text-[12.5px] text-ink-soft">Your PIN</Label>
              <Input type="password" inputMode="numeric" autoComplete="off" value={managerPin}
                onChange={(e) => setManagerPin(e.target.value.replace(/\D/g, '').slice(0, 6))} data-testid="staff-pin-manager-pin" /></div>
          )}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => { setStep(null); setAsk(null); }}>Cancel</Button>
            <Button className="bg-verlo hover:bg-verlo-hover" onClick={send} disabled={busy || (ask && !password)} data-testid="staff-pin-confirm">
              {ask ? 'Show the new PIN' : 'Send a new PIN'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Who reports to whom. Each person with a team has a chevron that folds it away and back.
function Chart({ people, collapsed, onToggle, onOpen }) {
  const kids = useMemo(() => {
    const m = {};
    people.forEach((p) => { if (p.reports_to) (m[p.reports_to] = m[p.reports_to] || []).push(p); });
    return m;
  }, [people]);
  const ids = new Set(people.map((p) => p.id));
  const rank = (p) => -rankOf(p);   // the most senior first
  const tops = people.filter((p) => !p.reports_to || !ids.has(p.reports_to)).sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
  const leaders = tops.filter((p) => kids[p.id]?.length || rankOf(p) > 0);
  const loose = tops.filter((p) => !leaders.includes(p));

  const node = (p, depth = 0) => {
    const team = (kids[p.id] || []).slice().sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
    const shut = collapsed.has(p.id);
    return (
      <div key={p.id}>
        <div className="flex items-center gap-1 border-b border-hairline py-2 pr-3 hover:bg-hover-tint" style={{ paddingLeft: `${8 + depth * 28}px` }} data-testid={`org-staff-${p.id}`}>
          {team.length ? (
            <button type="button" onClick={() => onToggle(p.id)} className="grid h-7 w-7 place-items-center rounded text-ink-soft hover:bg-white"
              aria-label={`${shut ? 'Show' : 'Hide'} ${p.name}'s team`} aria-expanded={!shut} data-testid={`org-toggle-${p.id}`}>
              {shut ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
            </button>
          ) : <span className="w-7" />}
          <button type="button" onClick={() => onOpen(p)} className="flex min-w-0 flex-1 items-center justify-between gap-3 text-left">
            <Person name={p.name} role={p.role} />
            <span className="shrink-0 text-[12.5px] text-ink-faint">{roleLabel(p.rbac_role)}{team.length ? ` · ${team.length} in their team` : ''}</span>
          </button>
        </div>
        {!shut && team.map((k) => node(k, depth + 1))}
      </div>
    );
  };

  if (!people.length) return <Notice tone="info" word="Team" title="Nobody on the team yet">Add the first person to see the chart.</Notice>;
  return (
    <div className="space-y-4">
      {/* Captioned like the group below it (Chris's review L13). */}
      <div className="border border-hairline bg-white" data-testid="team-chart">
        {leaders.length > 0 && <div className="border-b border-hairline px-4 py-2.5 text-[12.5px] text-ink-soft">Who reports to whom</div>}
        {leaders.map((p) => node(p))}
      </div>
      {loose.length > 0 && (
        <div className="border border-hairline bg-white">
          <div className="border-b border-hairline px-4 py-2.5 text-[12.5px] text-ink-soft">Not reporting to anyone yet. Open someone to set who they report to.</div>
          {loose.map((p) => node(p))}
        </div>
      )}
    </div>
  );
}
